import DashboardSummary from "components/DashboardSummary";
import FilterPanel from "components/FilterPanel";
import { Col as Column, Grid, Row } from "components/FlexBox/FlexBox";
import DonutChart from "components/Widgets/DonutChart/DonutChart";
import LineChart from "components/Widgets/LineChart/LineChart";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { useState } from "react";
import FadeLoader from "react-spinners/FadeLoader";
import { withStyle } from "theme/styletron";
dayjs.extend(utc);

const Col = withStyle(Column, () => ({
  "@media only screen and (max-width: 574px)": {
    marginBottom: "30px",

    ":last-child": {
      marginBottom: 0,
    },
  },
}));

export const noCallForAssistance = (item, hotelIds: string[]) => {
  const cfaDisabledHotelIds = [
    "6449e7e5abd582feb900edbd", // dmel
    "62fef2d3058c188c80ea0066", // dgc
    "6434be5690f99a6e3787ec96", // dmk
    "639ff0c5e7b1e3b2b42d2eed", // daoSg
  ];
  
  const noNeedCfaHotels = hotelIds.some(id => cfaDisabledHotelIds.includes(id));
  
  if (
    noNeedCfaHotels &&
    (item === "Call For Assistance" ||
      item?.label === "Call For Assistance" ||
      item?.name === "Call For Assistance")
  )
    return false;
  return item;
};

const Dashboard = ({ canAccessHotelList }) => {
  const [categories, setCategories] = useState([]);
  const [series, setSeries] = useState([]);
  const [pieChartSeries, setPieChartSeries] = useState([]);
  const [summary, setSummary] = useState([]);
  const [displayData, setDisplayData] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const mapDataToCategories = (data, responseType) => {
    if (responseType === "0") {
      return new Array(24)
        .fill(0)
        .map((value, index) => `${index < 10 ? `0${index}` : index}:00`);
    }

    return data.breakdown.map((breakdown) =>
      dayjs(breakdown.d || breakdown.date).format("MM-DD")
    );
  };

  const mapDataToSeries = (data) => {
    let returnArray = [
      {
        name: "Check In",
        key: "checkin",
        data: data?.breakdown?.map((breakdown) => breakdown.check_in),
      },
      {
        name: "Check Out",
        key: "checkout",
        data: data?.breakdown?.map((breakdown) => breakdown.check_out),
      },
      {
        name: "Check Out Failed",
        key: "checkoutFailed",
        data: data?.breakdown?.map((breakdown) => breakdown.check_out_failed),
      },
      {
        name: "Key Pick Up",
        key: "keyPickup",
        data: data?.breakdown?.map((breakdown) => breakdown.key_pickup),
      },
      {
        name: "Unsuccessful Key Pick Up",
        key: "keyPickupFailed",
        data: data?.breakdown?.map((breakdown) => breakdown.key_pickup_failed),
      },
      {
        name: "Wait For Room",
        key: "waitForRoom",
        data: data?.breakdown?.map((breakdown) => breakdown.wait_for_room),
      },
      {
        name: "Incomplete Check In",
        key: "incompleteCheckin",
        data: data?.breakdown?.map(
          (breakdown) => breakdown.incomplete_check_in
        ),
      },
    ];
    const temp = returnArray;
    return temp;
  };

  const mapDataToPieChartSeries = (data) => {
    const returnArray = [
      data.check_in || 0,
      data.check_out || 0,
      data.check_out_failed || 0,
      data.key_pickup || 0,
      data.key_pickup_failed || 0,
      data.wait_for_room || 0,
      data.incomplete_check_in || 0,
    ];
    return returnArray;
  };

  const mapDataToSummary = (data) => {
    const returnArray = [
      {
        label: "Check-In",
        content: data.check_in || 0,
      },
      {
        label: "Check-Out",
        content: data.check_out || 0,
      },
      {
        label: "Check-Out Failed",
        content: data.check_out_failed || 0,
      },
      {
        label: "Key Picked Up",
        content: data.key_pickup || 0,
      },
      {
        label: "Unsuccessful Key Picked Up",
        content: data.key_pickup_failed || 0,
      },
      {
        label: "Wait For Room",
        content: data.wait_for_room || 0,
      },
      {
        label: "Incomplete Check-In",
        content: data.incomplete_check_in || 0,
      },
      {
        label: "Total Number Of Usages",
        content: data.total_no_of_users || 0,
      },
      {
        label: "Average Success Check-In Duration",
        content: data.average_success_check_in_time_minutes
          ? data.average_success_check_in_time_minutes
          : "N/A",
        unit: data.average_success_check_in_time_minutes ? "mins" : "",
      },
      {
        label: "Average Success Check-Out Duration",
        content: data.average_success_check_out_time_minutes
          ? data.average_success_check_out_time_minutes
          : "N/A",
        unit: data.average_success_check_out_time_minutes ? "mins" : "",
      },
    ];
    return returnArray;
  };

  const filterSeries = (series) => {
    const temp = series.filter((item) => {
      return displayData[item.key];
    });
    return temp;
  };

  const onDataChange = (data, responseType, displayData) => {
    // add 2 hours for dgc & melbourne
    setCategories(mapDataToCategories(data, responseType));
    setSeries(mapDataToSeries(data));
    setPieChartSeries(mapDataToPieChartSeries(data));
    setSummary(mapDataToSummary(data));
    setDisplayData(displayData);
  };

  const getLineChartColor = () => {
    const colorOrder = [
      "checkin",
      "checkout",
      "checkoutFailed",
      "keyPickup",
      "keyPickupFailed",
      "waitForRoom",
      "incompleteCheckin",
    ];

    const colors = {
      checkin: "#FFD684",
      checkout: "#85E2FF",
      checkoutFailed: "#FF9AA2",
      keyPickup: "#B5EAD7",
      keyPickupFailed: "#FFB7C5",
      waitForRoom: "#FFC7EA",
      incompleteCheckin: "#F4B183",
    };

    return colorOrder
      .filter((key) => displayData[key]) // Only include keys that exist in displayData
      .map((key) => colors[key]);
  };

  // color={getLineChartColor()}
  // categories={categories}
  // series={filterSeries(series)}

  return (
    <Grid fluid={true}>
      <Row>
        <Col md={12} lg={12}>
          <FilterPanel
            onDataChange={onDataChange}
            onDisplayDataChange={setDisplayData}
            isLoading={isLoading}
            setIsLoading={setIsLoading}
            canAccessHotelList={canAccessHotelList}
          />
        </Col>
      </Row>
      {isLoading ? (
        <Row>
          <div
            style={{
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
            }}
          >
            <FadeLoader
              color={"gray"}
              loading={true}
              aria-label="Loading Spinner"
              data-testid="loader"
            />
          </div>
        </Row>
      ) : (
        <>
          <Row>
            <Col md={12} lg={12}>
              <LineChart
                color={getLineChartColor()}
                categories={categories}
                series={filterSeries(series)}
                noCallForAssistance={(item) => noCallForAssistance(item, canAccessHotelList?.map((h) => h.hotelId) || [])}
              />
            </Col>
          </Row>
          <Row>
            <Col md={7} lg={8}>
              <DashboardSummary data={summary} canAccessHotelList={canAccessHotelList} />
            </Col>

            <Col md={5} lg={4}>
              <DonutChart
                widgetTitle="Target"
                series={pieChartSeries}
                labels={[
                  "Check-In",
                  "Check-Out",
                  "Check-Out Failed",
                  "Key Picked Up",
                  "Unsuccessful Key Picked Up",
                  "Wait For Room",
                  "Incomplete Check-In",
                ]}
                colors={[
                  "#FFD684",
                  "#85E2FF",
                  "#FF9AA2",
                  "#B5EAD7",
                  "#FFB7C5",
                  "#FFC7EA",
                  "#F4B183",
                ]}
                prefix="$"
              />
            </Col>
          </Row>
        </>
      )}
    </Grid>
  );
};

export default Dashboard;
